'use client';
import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import FormTextarea from '@c/forms/controls/FormTextarea';
import BaseModal from '@c/modals/BaseModal';
import { useQuery } from '@tanstack/react-query';
import Button from '@ui/Button';
import Checkbox from '@ui/Checkbox';
import SafeImage from '@ui/SafeImage';
import { functions, httpsCallable } from '@util/firebase';
import {
  SMART_LISTING_VERSION,
  submitFeedback,
} from '@util/firestore/listing/listing.service';
import {
  getCategoryByProductCats,
  getProductByMpn,
  getProductId,
} from '@util/firestore/products';
import { formatCurrency, normalize } from '@util/index';
import { logError } from '@util/logError';
import ProductFitmentTab from 'app/product/[slug]/components/tabs/ProductFitmentTab';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { AlertTriangle, SearchIcon, ShieldCheckIcon } from 'lucide-react';
import { ProductTemplate } from 'models/product';
import { LETTER_SIZES } from 'models/shared';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useCreateListing } from '../_context';

export default function CreateListingSearch() {
  const { userDoc } = useAuth();
  const {
    goToStep,
    logSmartListingEventMutation,
    searchId,
    setSearchTerm,
    debouncedSearchTerm,
    searchTerm,
  } = useCreateListing();

  const [limit, setLimit] = useState(5);
  const { data, isLoading } = useQuery({
    queryKey: ['smart-listing-products', debouncedSearchTerm],
    queryFn: async () => {
      const start = performance.now();
      const results = await httpsCallable<
        { term: string },
        { results: ProductTemplate[]; active_listings: ProductTemplate[] }
      >(
        functions,
        'listingTool-v2_smartListingFromTerm'
      )({
        term: debouncedSearchTerm,
      });
      const end = performance.now();
      logSmartListingEventMutation.mutateAsync({
        event: 'search_results_served',
        session_id: '',
        search_id: searchId,
        event_id: window.crypto.randomUUID(),
        uid: userDoc?.uid ?? '',
        device: window.innerWidth > 768 ? 'desktop' : 'mobile',
        smart_listing_version: SMART_LISTING_VERSION,
        results_count: results.data.results.length,
        latency_ms: Math.floor(end - start),
        top_results: results.data.results.slice(0, 5).map((r, i) => ({
          id: r.id,
          score: r.score ?? 0,
          data_source:
            r.data_source || r.slug.includes('https')
              ? 'data_depot'
              : 'active_listings',
          rank: i + 1,
        })),
      });
      return results;
    },
    enabled: !!debouncedSearchTerm,
  });

  const paginatedHits = data?.data.results.slice(0, limit);
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({
    searchTerm: '',
    feedback: '',
    isBug: false,
  });
  const { showSuccessToast, showErrorToast } = useToastContext();

  // ANALYTICS
  // Start / Leave Events
  useEffect(() => {
    if (!userDoc?.uid) return;
    logSmartListingEventMutation.mutate({
      event: 'start',
      session_id: '',
      search_id: searchId,
      event_id: window.crypto.randomUUID(),
      uid: userDoc?.uid ?? '',
      device: window.innerWidth > 768 ? 'desktop' : 'mobile',
      smart_listing_version: SMART_LISTING_VERSION,
    });
  }, [userDoc?.uid]);

  useEffect(() => {
    if (!userDoc?.uid || !debouncedSearchTerm) return;
    logSmartListingEventMutation.mutate({
      event: 'search_performed',
      session_id: '',
      search_id: searchId,
      event_id: window.crypto.randomUUID(),
      uid: userDoc?.uid ?? '',
      device: window.innerWidth > 768 ? 'desktop' : 'mobile',
      smart_listing_version: SMART_LISTING_VERSION,
      query_raw: debouncedSearchTerm,
      query_norm: normalize(debouncedSearchTerm),
    });
  }, [debouncedSearchTerm, userDoc?.uid]);
  return (
    <div className="mx-auto my-3 flex max-w-[100rem] flex-col gap-[2rem] px-[2rem] sm:my-[8rem] ">
      <div className="flex w-full flex-col-reverse items-center justify-between gap-6 sm:flex-row">
        <h1 className="self-start text-[3rem] font-bold">
          What are you selling?
        </h1>
        <button
          onClick={() => setIsOpen(true)}
          className="self-end text-[1.75rem] font-bold text-brand-secondary"
        >
          Give Feedback
        </button>
        <BaseModal
          title="Give Feedback"
          isOpen={isOpen}
          dismiss={() => setIsOpen(false)}
        >
          <div className="flex flex-col gap-4 p-8 sm:min-w-[50rem]">
            <FormLabel value="What are you trying to sell?">
              <FormInput
                placeholder="Item name or SKU"
                value={form.searchTerm}
                onChange={(e) =>
                  setForm({ ...form, searchTerm: e.target.value })
                }
              />
            </FormLabel>
            <FormLabel value="Additional Information">
              <FormTextarea
                placeholder="Enter your feedback here..."
                value={form.feedback}
                onChange={(e) => setForm({ ...form, feedback: e.target.value })}
              />
            </FormLabel>
            <Checkbox
              label="I'm reporting a bug"
              selected={form.isBug}
              onChange={() => setForm({ ...form, isBug: !form.isBug })}
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="tertiary"
                text="Cancel"
                onClick={() => setIsOpen(false)}
              />
              <Button
                type="secondary"
                text="Submit"
                disabled={!form.feedback || !form.searchTerm}
                onClick={async () => {
                  // Handle submit logic here
                  try {
                    await submitFeedback(
                      form.feedback,
                      form.isBug,
                      form.searchTerm,
                      userDoc?.uid ?? ''
                    );
                    setIsOpen(false);
                    showSuccessToast('Feedback submitted');
                  } catch (e) {
                    logError(e);
                    showErrorToast(
                      'Failed to submit feedback. Please try again later.'
                    );
                  }
                }}
              />
            </div>
          </div>
        </BaseModal>
      </div>
      <div className="relative flex w-full overflow-hidden rounded-[1.2rem] border border-gray-600 bg-white">
        <input
          placeholder="Type item name or SKU"
          className="relative h-[5.5rem] grow border-none bg-white px-[2rem] text-[2rem] outline-none"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <SearchIcon
          fill="white"
          className="absolute right-[2rem] top-[50%] translate-y-[-50%] bg-white"
        />
      </div>
      {isLoading && (
        <div className="flex flex-col gap-4">
          <div className="flex h-[10rem] w-full gap-4 rounded-md bg-white p-4 shadow-card">
            <div className="h-[8rem] w-[8rem] animate-pulse rounded-md bg-gray-400" />
            <div className="flex w-full flex-col gap-4">
              <div className="h-[2rem] w-full animate-pulse rounded-md bg-gray-400" />
              <div className="h-[2rem] w-full animate-pulse rounded-md bg-gray-400" />
            </div>
          </div>
          <div className="flex h-[10rem] w-full gap-4 rounded-md bg-white p-4 shadow-card">
            <div className="h-[8rem] w-[8rem] animate-pulse rounded-md bg-gray-400" />
            <div className="flex w-full flex-col gap-4">
              <div className="h-[2rem] w-full animate-pulse rounded-md bg-gray-400" />
              <div className="h-[2rem] w-full animate-pulse rounded-md bg-gray-400" />
            </div>
          </div>
          <div className="flex h-[10rem] w-full gap-4 rounded-md bg-white p-4 shadow-card">
            <div className="h-[8rem] w-[8rem] animate-pulse rounded-md bg-gray-400" />
            <div className="flex w-full flex-col gap-4">
              <div className="h-[2rem] w-full animate-pulse rounded-md bg-gray-400" />
              <div className="h-[2rem] w-full animate-pulse rounded-md bg-gray-400" />
            </div>
          </div>
          <div className="flex h-[10rem] w-full gap-4 rounded-md bg-white p-4 shadow-card">
            <div className="h-[8rem] w-[8rem] animate-pulse rounded-md bg-gray-400" />
            <div className="flex w-full flex-col gap-4">
              <div className="h-[2rem] w-full animate-pulse rounded-md bg-gray-400" />
              <div className="h-[2rem] w-full animate-pulse rounded-md bg-gray-400" />
            </div>
          </div>
        </div>
      )}
      <SearchResults
        hits={paginatedHits ?? []}
        totalHits={data?.data.results?.length ?? 0}
        debouncedSearchTerm={debouncedSearchTerm}
        setLimit={setLimit}
        activeListings={data?.data.active_listings ?? []}
      />
      <div className="mt-16 flex w-full justify-center">
        <button
          className="flex w-[25rem] grow items-center justify-center gap-x-2 rounded-[1.5rem] bg-brand-lightest-gray px-6 py-3 text-[1.6rem] text-black hover:bg-brand-light-gray sm:grow-0 sm:px-8 sm:py-4 sm:text-[1.8rem]"
          type="button"
          onClick={() => goToStep('select-listing-type')}
        >
          Back
        </button>
      </div>
    </div>
  );
}

function SearchResults({
  hits,
  totalHits,
  debouncedSearchTerm,
  setLimit,
  activeListings,
}: {
  hits: ProductTemplate[];
  totalHits: number;
  debouncedSearchTerm: string;
  setLimit: Dispatch<SetStateAction<number>>;
  activeListings?: ProductTemplate[];
}) {
  const [selectedProduct, setSelectedProduct] =
    useState<ProductTemplate | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [missingColor, setMissingColor] = useState(false);
  const [selectedSize, setSelectedSize] = useState<string | number | null>(
    null
  );
  const [missingSize, setMissingSize] = useState(false);
  const { goToStep, form, setCategoryInfo, setSourceVariations } =
    useCreateListing();
  const { userDoc } = useAuth();

  // Clear selected product when search term changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedProduct(null);
    setSelectedColor(null);
    setSelectedSize(null);
    setMissingColor(false);
    setMissingSize(false);
  }, [debouncedSearchTerm]);
  const isLetterSize = selectedProduct?.variations?.some(
    (v) => v.size !== 'N/A' && !v.is_number
  );
  const isNumberSize = selectedProduct?.variations?.some(
    (v) => v.size !== 'N/A' && v.is_number
  );

  // Size options come from the product's actual variations (filtered by the
  // selected color) so the seller can only pick a size that really exists.
  const getSizesForColor = (color: string | null) => {
    const variations = selectedProduct?.variations ?? [];
    const pool =
      color && color !== 'Other'
        ? variations.filter((v) => v.color === color)
        : variations;
    const source = pool.length ? pool : variations;
    return Array.from(
      new Set(source.filter((v) => v.size !== 'N/A').map((v) => v.size))
    ).sort((a, b) => {
      const ai = LETTER_SIZES.indexOf(`${a}`);
      if (ai !== -1) return ai - LETTER_SIZES.indexOf(`${b}`);
      return Number(a) - Number(b);
    });
  };
  const availableSizes = getSizesForColor(selectedColor);

  const { logSmartListingEventMutation, searchId } = useCreateListing();
  return (
    <>
      {selectedProduct ? (
        <div className="flex flex-col gap-[3rem]">
          <div className="flex w-full flex-col gap-[2rem] rounded-[1.5rem] bg-white p-[3rem] shadow-card">
            <div className="flex w-full justify-between">
              <div className="flex items-center gap-[1rem]">
                <p className="text-[2.5rem] font-bold">Title</p>
                <div className="flex items-center rounded-[0.5rem] bg-[#E9F2E9] px-[1rem] py-[0.25rem] align-middle text-[1.875rem] leading-[150%] text-[#1E7A1E]">
                  Suggested by MX Locker
                </div>
                {!!userDoc?.roles?.support && (
                  <div className="flex items-center rounded-[0.5rem] bg-zinc-100 px-[1rem] py-[0.25rem] align-middle text-[1.875rem] leading-[150%] text-zinc-700">
                    {selectedProduct.slug.includes('revzilla')
                      ? 'RevZilla'
                      : selectedProduct.data_source ?? 'Data Depot'}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col">
              {!!selectedProduct.group_title && (
                <span className="text-[1.75rem] font-medium text-[#7F7F7F]">
                  {selectedProduct.group_title}
                </span>
              )}
              <span className="text-[2rem] font-medium">
                {selectedProduct.title}
              </span>
              <span className="text-[1.75rem] font-medium text-[#7F7F7F]">
                {selectedProduct.brand}
              </span>
              <br />
              {!!selectedProduct.mpn?.length && (
                <>
                  <strong className="text-[1.75rem] font-bold text-black">
                    Manufacturer Part Number(s):{' '}
                  </strong>
                  <span className="line-clamp-1 text-[1.75rem] font-medium text-[#7F7F7F]">
                    {selectedProduct.mpn?.join(', ')}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex w-full flex-col gap-[2rem] rounded-[1.5rem] bg-white p-[3rem] shadow-card">
            <div className="flex w-full justify-between">
              <div className="flex items-center gap-[1rem]">
                <p className="text-[2.5rem] font-bold">Category</p>
                <div className="flex items-center rounded-[0.5rem] bg-[#E9F2E9] px-[1rem] py-[0.25rem] align-middle text-[1.875rem] leading-[150%] text-[#1E7A1E]">
                  Suggested by MX Locker
                </div>
              </div>
            </div>

            <span className="text-start text-[2rem] font-medium">
              {selectedProduct.categories
                .join(' > ')
                .replace('Bike Parts', 'Parts')}
            </span>
          </div>
          {selectedProduct.category === 'Bike Parts' &&
            !!selectedProduct.fitting_bikes?.bikes.length && (
              <div className="flex w-full flex-col  rounded-[1.5rem] bg-white p-[3rem] shadow-card">
                <div className="flex w-full justify-between">
                  <div className="flex flex-col gap-[1rem] sm:flex-row sm:items-center">
                    <p className="text-[2.5rem] font-bold">Fitment</p>
                    {selectedProduct.sources?.find(
                      (source) => source.collection === 'data_depot'
                    ) ? (
                      <div className="flex items-center gap-2 whitespace-nowrap rounded-[0.5rem] bg-[#E9F2E9] px-[1rem] py-[0.25rem] align-middle text-[1.875rem] leading-[150%] text-[#1E7A1E]">
                        <ShieldCheckIcon
                          width={20}
                          height={20}
                          fill={'#1E7A1E'}
                          stroke={'#E9F2E9'}
                        />{' '}
                        Guaranteed by MX Locker
                      </div>
                    ) : (
                      <div className="flex items-center gap-6 rounded-[0.5rem] bg-yellow-500/50 px-[1rem] py-[0.5rem] align-middle text-[1.6rem] leading-[150%] text-black sm:text-[1.875rem]">
                        <AlertTriangle
                          width={20}
                          height={20}
                          className="flex-shrink-0"
                          fill={'yellow'}
                          stroke={'black'}
                        />
                        Fitment provided by verified sellers. Please verify
                        fitment before listing.
                      </div>
                    )}
                  </div>
                </div>
                <ProductFitmentTab product={selectedProduct} />
              </div>
            )}
          {selectedProduct.variations?.some((v) => v.color !== 'N/A') && (
            <div
              className={`flex w-full flex-col gap-[2rem] rounded-[1.5rem] bg-white p-[3rem] shadow-card ${
                missingColor ? 'border-2 border-red-500' : ''
              }`}
            >
              <div className="flex w-full justify-between">
                <div className="flex items-center gap-[1rem]">
                  <p className="text-[2.5rem] font-bold">Colors</p>
                  <div className="flex items-center gap-2 rounded-[0.5rem] bg-[#E9F2E9] px-[1rem] py-[0.25rem] align-middle text-[1.875rem] leading-[150%] text-[#1E7A1E]">
                    From MX Locker&apos;s Database
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                {Array.from(
                  new Set(selectedProduct.variations.map((v) => v.color))
                ).map((color) => {
                  const image = selectedProduct.variations?.find(
                    (v) => v.color === color
                  )?.image;
                  return (
                    <button
                      onClick={() => {
                        setSelectedColor(color!);
                        setMissingColor(false);
                        if (
                          selectedSize != null &&
                          !getSizesForColor(color ?? null).includes(
                            selectedSize
                          )
                        ) {
                          setSelectedSize(null);
                        }
                      }}
                      key={color}
                      className={`flex items-center gap-[1rem] rounded-[1rem] border-[1px] ${
                        selectedColor === color
                          ? 'border-brand-secondary text-brand-secondary'
                          : 'border-zinc-200'
                      } p-[0.5rem] pl-[0.5rem] ${
                        image ? 'pr-[2rem]' : 'pr-[0.5rem]'
                      } font-medium`}
                    >
                      {image && (
                        <div className="relative h-[5rem] w-[5rem] rounded-[1rem] p-1">
                          <SafeImage
                            key={image}
                            src={image}
                            alt={color!}
                            fill
                            className="object-contain"
                          />
                        </div>
                      )}
                      {color}
                    </button>
                  );
                })}
                <button
                  onClick={() => {
                    setSelectedColor('Other');
                    setMissingColor(false);
                  }}
                  key="other"
                  className={`flex ${
                    selectedColor === 'Other'
                      ? 'border-brand-secondary text-brand-secondary'
                      : 'border-zinc-200'
                  } items-center gap-[1rem] rounded-[1rem] border-[1px] p-[0.5rem] pl-[0.5rem] pr-[0.5rem] font-medium`}
                >
                  Other
                </button>
              </div>
              {missingColor && (
                <span className="text-[1.75rem] font-medium text-red-500">
                  Please select a color
                </span>
              )}
            </div>
          )}
          {(isLetterSize || isNumberSize) && (
            <div
              className={`flex w-full flex-col gap-[2rem] rounded-[1.5rem] bg-white p-[3rem] shadow-card ${
                missingSize ? 'border-2 border-red-500' : ''
              }`}
            >
              <div className="flex w-full justify-between">
                <div className="flex items-center gap-[1rem]">
                  <p className="text-[2.5rem] font-bold">Size</p>
                  <div className="flex items-center gap-2 rounded-[0.5rem] bg-[#E9F2E9] px-[1rem] py-[0.25rem] align-middle text-[1.875rem] leading-[150%] text-[#1E7A1E]">
                    From MX Locker&apos;s Database
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                {availableSizes.map((size) => {
                  return (
                    <button
                      onClick={() => {
                        setSelectedSize(size);
                        setMissingSize(false);
                      }}
                      key={size}
                      className={`flex items-center gap-[1rem] rounded-[1rem] border-[1px] ${
                        selectedSize === size
                          ? 'border-brand-secondary text-brand-secondary'
                          : 'border-zinc-200'
                      } p-[1rem]  px-[3rem] font-medium`}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
              {missingSize && (
                <span className="text-[1.75rem] font-medium text-red-500">
                  Please select a size
                </span>
              )}
            </div>
          )}
          <span className="text-center text-[1.5rem] font-medium text-[#7F7F7F]">
            You can edit the details in the next step.
          </span>
          <div className="flex w-full flex-col-reverse gap-8 sm:flex-row">
            <Button
              text="I'll start from scratch"
              type="outline"
              width="fluid"
              onClick={() => {
                logSmartListingEventMutation.mutateAsync({
                  event: 'start_from_scratch_clicked',
                  session_id: '',
                  search_id: searchId,
                  event_id: window.crypto.randomUUID(),
                  uid: userDoc?.uid ?? '',
                  device: window.innerWidth > 768 ? 'desktop' : 'mobile',
                  smart_listing_version: SMART_LISTING_VERSION,
                });
                goToStep('select-listing-sport');
              }}
            />
            <Button
              text="Continue Listing"
              type="secondary"
              width="fluid"
              onClick={async () => {
                if (!userDoc) return;
                if (
                  selectedProduct.variations?.length &&
                  selectedProduct.variations.some((v) => v.color) &&
                  !selectedColor
                ) {
                  setMissingColor(true);
                  return;
                }
                if (
                  selectedProduct.variations?.length &&
                  selectedProduct.variations.some((v) => v.size !== 'N/A') &&
                  !selectedSize
                ) {
                  setMissingSize(true);
                  return;
                }

                const matchingVariation = selectedProduct.variations?.find(
                  (v) =>
                    (!selectedColor || v.color === selectedColor) &&
                    (!selectedSize || v.size === selectedSize)
                );
                const size = isLetterSize
                  ? { letter: selectedSize as string }
                  : isNumberSize
                  ? { number: Number(selectedSize) }
                  : null;
                logSmartListingEventMutation.mutateAsync({
                  event: 'continue_listing_clicked',
                  session_id: '',
                  search_id: searchId,
                  event_id: window.crypto.randomUUID(),
                  uid: userDoc?.uid ?? '',
                  device: window.innerWidth > 768 ? 'desktop' : 'mobile',
                  smart_listing_version: SMART_LISTING_VERSION,
                });
                const now = Date.now();
                form.reset({
                  id: getProductId(),
                  date_added: now,
                  account_id: userDoc.account_id ?? '',
                  bumps: 0,
                  created: now,
                  date_updated: now,
                  from_web: true,
                  has_variations: false,
                  hash: '',
                  images: [],
                  gender: selectedProduct.gender,
                  is_auction: false,
                  is_digital: false,
                  is_featured: false,
                  is_flat_rate: false,
                  is_draft: false,
                  out_of_stock: false,
                  ride_types: selectedProduct.ride_types,
                  favorite_count: 0,
                  seller_id: userDoc.uid ?? '',
                  seller_type: userDoc.seller_type ?? 'individual',
                  slug: '',
                  sku: '',
                  mpn: matchingVariation?.mpn
                    ? matchingVariation.mpn
                    : selectedProduct.mpn ?? [],
                  brand: selectedProduct.brand,
                  thumbnail: selectedProduct.thumbnail,
                  og_price: 1,
                  price: 0,
                  on_sale: false,
                  ...(size !== null && { size }),
                  ...(selectedColor !== null && {
                    color: selectedColor,
                  }),
                  fitting_bikes: selectedProduct.fitting_bikes,
                  title: selectedProduct.title,
                  // description: selectedProduct.description,
                  description: '',
                  category: selectedProduct.category,
                  categories: selectedProduct.categories,
                  category1: selectedProduct.category1,
                  category2: selectedProduct.category2,
                  category3: selectedProduct.category3,
                  condition: 'Used',
                  country_code: userDoc?.country.code ?? 'US',
                  sources: selectedProduct.sources,
                  stock: 1,
                  ...(selectedProduct.shipment?.package && {
                    shipment: {
                      package: selectedProduct.shipment.package,
                      ship_from: userDoc?.addresses[0],
                    },
                  }),
                });

                // Keep the catalog variations so step 3 can re-derive the
                // MPN if the seller changes the size/color of the listing.
                setSourceVariations(selectedProduct.variations ?? null);

                goToStep('create-listing');
                window.scrollTo(0, 0);
                const catDoc = await getCategoryByProductCats({
                  category: selectedProduct.category,
                  category1: selectedProduct.category1,
                  category2: selectedProduct.category2,
                  category3: selectedProduct.category3,
                  ride_types: selectedProduct.ride_types,
                });
                if (catDoc) {
                  setCategoryInfo([catDoc]);
                }
              }}
            />
          </div>
          <button
            type="button"
            className="text-[1.75rem] font-bold text-brand-secondary"
            onClick={() => setSelectedProduct(null)}
          >
            Back to results
          </button>
        </div>
      ) : (
        <>
          {debouncedSearchTerm &&
            hits.map((hit, i) => (
              <SearchHit
                key={hit.id}
                hit={hit}
                isBestMatch={!!hit.mpn?.includes(debouncedSearchTerm)}
                onClick={async () => {
                  // already a data depot product
                  logSmartListingEventMutation.mutateAsync({
                    event: 'result_clicked',
                    session_id: '',
                    search_id: searchId,
                    event_id: window.crypto.randomUUID(),
                    uid: userDoc?.uid ?? '',
                    device: window.innerWidth > 768 ? 'desktop' : 'mobile',
                    smart_listing_version: SMART_LISTING_VERSION,
                    result_id: hit.id,
                    result_data_source:
                      (hit as any).data_source || hit.slug.includes('https')
                        ? 'data_depot'
                        : 'active_listings',
                    result_rank: i + 1,
                    result_score: hit.score ?? 0,
                  });
                  if ((hit as any).data_source || hit.slug.includes('https')) {
                    setSelectedProduct({
                      ...hit,
                      sources: [
                        {
                          id: hit.id,
                          collection: 'data_depot',
                        },
                      ],
                    });
                  } else {
                    const foundMpn =
                      hit.mpn?.find((mpn) => mpn === debouncedSearchTerm) ||
                      hit.mpn?.[0];
                    if (foundMpn && hit.category === 'Bike Parts') {
                      const foundDataDepot = await getProductByMpn(
                        foundMpn.includes(',')
                          ? foundMpn.split(',')
                          : [foundMpn]
                      );
                      if (foundDataDepot) {
                        setSelectedProduct({
                          ...hit,
                          fitting_bikes: foundDataDepot.fitting_bikes,
                          ride_types: foundDataDepot.ride_types,
                          mpn: foundDataDepot.mpn,
                          sources: [
                            {
                              id: foundDataDepot.id,
                              collection: 'data_depot',
                            },
                            {
                              id: hit.id,
                              collection: 'products',
                            },
                          ],
                        });
                      } else {
                        setSelectedProduct({
                          ...hit,
                          sources: [
                            {
                              id: hit.id,
                              collection: 'products',
                            },
                          ],
                        });
                      }
                    } else {
                      setSelectedProduct({
                        ...hit,
                        sources: [
                          {
                            id: hit.id,
                            collection: 'products',
                          },
                        ],
                      });
                    }
                  }
                }}
              />
            ))}
          {hits.length < totalHits && (
            <Button
              text="View More"
              type="text"
              className="text-[1.75rem] font-bold text-brand-secondary"
              onClick={() => {
                setLimit((prev) => prev + 5);
              }}
            />
          )}
          {activeListings && activeListings.length > 0 && hits.length !== 0 && (
            <div className="flex w-full flex-col gap-4">
              <p className="text-[1.75rem] font-bold sm:text-[2rem]">
                Listings on MX Locker with &quot;{debouncedSearchTerm}&quot;
              </p>

              <div
                className={`sm:grid sm:grid-cols-${activeListings.length} sm:overflow-x-none flex w-full gap-4 overflow-x-auto`}
              >
                {activeListings?.map((listing) => (
                  <div
                    className="relative h-[15rem] w-[15rem] flex-shrink-0"
                    key={listing.id}
                  >
                    <SafeImage
                      src={listing.thumbnail}
                      alt={listing.title}
                      fill
                      className="object-cover"
                    />
                    <div className="absolute bottom-[1rem] left-[1rem] rounded-full bg-brand-secondary p-4  font-semibold text-white">
                      {formatCurrency(listing.price)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {((debouncedSearchTerm && hits.length > 0) ||
            !debouncedSearchTerm) && (
            <>
              <div className="separator">OR</div>
              <div className="mx-auto flex flex-col items-center gap-4">
                {debouncedSearchTerm && hits.length > 0 && (
                  <p className="text-[2.25rem] font-bold">
                    Not seeing what you&apos;re looking for?
                  </p>
                )}
                <Button
                  type="secondary"
                  text="Start Listing From Scratch"
                  onClick={() => {
                    logSmartListingEventMutation.mutateAsync({
                      event: 'start_from_scratch_clicked',
                      session_id: '',
                      search_id: searchId,
                      event_id: window.crypto.randomUUID(),
                      uid: userDoc?.uid ?? '',
                      device: window.innerWidth > 768 ? 'desktop' : 'mobile',
                      smart_listing_version: SMART_LISTING_VERSION,
                    });
                    goToStep('select-listing-sport');
                  }}
                  width="small"
                  height="small"
                  className="h-[5.5rem] w-full text-[2.1rem] sm:w-[48rem]"
                />
              </div>
            </>
          )}
          {hits.length === 0 && debouncedSearchTerm && <NoMatchFound />}
        </>
      )}
    </>
  );
}

const SearchHit = ({
  hit,
  isBestMatch,
  onClick,
}: {
  hit: ProductTemplate;
  isBestMatch: boolean;
  onClick: () => void;
}) => {
  const { userDoc } = useAuth();
  return (
    <button
      type="button"
      className="group flex w-full gap-[1.5rem] rounded-[1.5rem] border-[1px] border-white bg-white py-[1.5rem] pl-[1.5rem] pr-[3rem] hover:border-[1px] hover:border-brand-secondary"
      onClick={onClick}
    >
      <SafeImage
        src={hit.thumbnail}
        key={hit.thumbnail}
        className="my-auto h-[8rem] w-[8rem] overflow-clip rounded-[1.5rem] object-contain sm:my-0 sm:h-[12rem] sm:w-[12rem]"
        alt="product image"
      />
      <div className="relative flex w-full flex-col items-start gap-[0.5rem]">
        {!!hit.group_title && (
          <span className="text-left text-[1.5rem] font-medium text-[#7F7F7F]">
            {hit.group_title}
          </span>
        )}
        <div className="flex items-start gap-[1.25rem] text-left">
          {hit.highlights?.find((h) => h.field === 'title')?.snippet ? (
            <span
              className="text-1.5rem]  line-clamp-1 text-[#7F7F7F] sm:pr-[8rem] [&_mark]:bg-[#FFD480]"
              dangerouslySetInnerHTML={{
                __html:
                  hit.highlights.find((h) => h.field === 'title')?.snippet ??
                  '',
              }}
            />
          ) : (
            <p className="text-[1.5rem]sm:text-[2rem]">{hit.title}</p>
          )}
          {isBestMatch && (
            <div className="whitespace-nowrap rounded-[0.5rem] bg-[#E9F2E9] px-[1rem]">
              Exact Match
            </div>
          )}
          {!!userDoc?.roles?.support && (
            <div className="whitespace-nowrap rounded-[0.5rem] bg-zinc-100 px-[1rem] text-zinc-700">
              {hit.slug.includes('revzilla')
                ? 'RevZilla'
                : hit.data_source ?? 'Data Depot'}
            </div>
          )}
        </div>
        <span className="text-start text-[1.5rem] font-medium text-[#7F7F7F]">
          {hit.categories.join(' > ').replace('Bike Parts', 'Parts')}
        </span>
        {!!hit.mpn?.length && (
          <div className="mt-4 flex flex-col items-start gap-[0.5rem] text-left">
            <span className="text-[1.5rem] font-bold text-black">
              Manufacturer Part Number(s):{' '}
            </span>
            {hit.highlights?.find((h) => h.field === 'mpn')?.snippet ? (
              <span
                className="text-1.5rem]  line-clamp-1 pr-[8rem] text-[#7F7F7F] [&_mark]:bg-[#FFD480]"
                dangerouslySetInnerHTML={{
                  __html:
                    hit.highlights.find((h) => h.field === 'mpn')?.snippet ??
                    '',
                }}
              />
            ) : hit.highlights?.find((h) => h.field === 'sku')?.snippet ? (
              <span
                className="text-1.5rem]  line-clamp-1 pr-[8rem] text-[#7F7F7F] [&_mark]:bg-[#FFD480]"
                dangerouslySetInnerHTML={{
                  __html:
                    hit.highlights.find((h) => h.field === 'sku')?.snippet ??
                    '',
                }}
              />
            ) : (
              <span className="text-1.5rem]  line-clamp-1 pr-[8rem] text-[#7F7F7F]">
                {hit.mpn?.join(', ')}
              </span>
            )}
          </div>
        )}
        {hit.variations?.some((v) => v.image) && (
          <div className="flex flex-wrap gap-[0.6rem]">
            {Array.from(
              new Set(
                hit.variations?.filter((v) => v.image).map((v) => v.image)
              )
            ).map((img, i) => (
              <div
                className="h-[4rem] w-[4rem] rounded-[0.5rem] border-[1px] border-[#D9D9D9]"
                key={img! + i}
              >
                <SafeImage
                  src={img}
                  width={30}
                  height={30}
                  className="m-auto h-[3.75rem] w-[3.75rem] object-contain"
                  alt="extra product info"
                />
              </div>
            ))}
          </div>
        )}
        <div className="absolute right-0  hidden h-full items-center justify-center group-hover:flex">
          <p className="font-semibold text-brand-secondary">View Product</p>
        </div>
      </div>
    </button>
  );
};

const NoMatchFound = () => {
  const { goToStep, logSmartListingEventMutation, searchId } =
    useCreateListing();
  const { userDoc } = useAuth();
  return (
    <div className="flex h-[30rem] flex-col items-center justify-center gap-[3rem]">
      <div className="flex flex-col items-center gap-[0.75rem]">
        <p className="text-[2.25rem] font-bold">
          No match found in our database
        </p>
        <span>Please refine your search or start a listing from scratch</span>
      </div>
      <Button
        type="secondary"
        text="Start Listing From Scratch"
        onClick={() => {
          logSmartListingEventMutation.mutateAsync({
            event: 'start_from_scratch_clicked',
            session_id: '',
            search_id: searchId,
            event_id: window.crypto.randomUUID(),
            uid: userDoc?.uid ?? '',
            device: window.innerWidth > 768 ? 'desktop' : 'mobile',
            smart_listing_version: SMART_LISTING_VERSION,
          });
          goToStep('select-listing-sport');
        }}
        className="h-[5.5rem] w-full text-[2.1rem] sm:w-[48rem]"
      />
    </div>
  );
};
